import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastGlobalStatsUpdate, setLastGlobalStatsUpdate] = useState(null);
  const [lastGoalProgressUpdate, setLastGoalProgressUpdate] = useState(null);
  const [lastReadingReportCreated, setLastReadingReportCreated] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL;
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

    let baseUrl;
    if (import.meta.env.PROD) {
      baseUrl = window.location.origin;
    } else if (WEBSOCKET_URL) {
      baseUrl = WEBSOCKET_URL;
    } else if (API_URL.startsWith('http')) {
      baseUrl = API_URL.replace(/\/api$/, '');
    } else {
      baseUrl = 'http://localhost:3000';
    }
    
    // Create Socket.IO connection
    socketRef.current = io(baseUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    const socket = socketRef.current;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('🔌 WebSocket connected to:', baseUrl);
      setIsConnected(true);
      
      // Join the global stats room for live updates
      socket.emit('join-global-stats');
    });

    socket.on('disconnect', () => {
      console.log('🔌 WebSocket disconnected from:', baseUrl);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('🔌 WebSocket connection error:', error);
      console.error('🔌 Attempted to connect to:', baseUrl);
      setIsConnected(false);
      
      // If it's a namespace error, try reconnecting without namespace
      if (error.message && error.message.includes('Invalid namespace')) {
        console.log('🔄 Attempting to reconnect without namespace...');
        setTimeout(() => {
          if (socketRef.current) {
            socketRef.current.connect();
          }
        }, 2000);
      }
    });

    // Global stats update handler
    socket.on('global-stats-updated', (data) => {
      console.log('📊 Received global stats update:', data);
      setLastGlobalStatsUpdate(data);
    });

    // Goal progress update handler
    socket.on('goal-progress-updated', (data) => {
      console.log('🎯 Received goal progress update:', data);
      setLastGoalProgressUpdate(data);
    });

    // Reading report created handler
    socket.on('reading-report-created', (data) => {
      console.log('📖 Received reading report created event:', data);
      setLastReadingReportCreated(data);
    });

    // Bulk reading reports created handler
    socket.on('bulk-reading-reports-created', (data) => {
      console.log('📖 Received bulk reading reports created event:', data);
      setLastReadingReportCreated(data);
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.emit('leave-global-stats');
        socket.disconnect();
      }
    };
  }, []);

  const value = {
    isConnected,
    lastGlobalStatsUpdate,
    lastGoalProgressUpdate,
    lastReadingReportCreated,
    socket: socketRef.current
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
